import { useEffect, useState } from "react";
import { InvoicesHistoryData, TableFilterOption } from "@/types";
import { $archivedInvoices, $customers, $invoices } from "@/stores";
import { invoicesRepository } from "@/database/repositories/invoicesRepository";
import { ConfirmRestore } from "@/shared/confirmRestore/ConfirmRestore";
import { Flip, toast, ToastContainer } from "react-toastify";
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    TableMeta,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import "./invoicesArchive.scss";
import { DataTableToolbar } from "@/shared/table/TableToolbar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/table/TableElements";
import { DataTablePagination } from "@/shared/table/TablePagination";
import { Undo2 } from "lucide-react";
import { customersRepository } from "@/database/repositories/customersRepository";
import { archivedInvoicesColumns } from "./ArchiveInvoiceColumns";
import { useSearchParams } from "react-router-dom";
import { useDb } from "@/database/provider/useDb";
import { Spinner } from "@/shared/spinner/Spinner";

export interface ArchivedInvoicesTableMeta extends TableMeta<InvoicesHistoryData> {
    restoreInvoice: (invoiceNumber: string) => Promise<void>,
    openOptionsId: string | null,
    setOpenOptionsId: React.Dispatch<React.SetStateAction<string | null>>,
    deleteInvoice: (invoiceNumber: string) => Promise<void>
}


export const InvoicesArchive = () => {
    const db = useDb();
    const [searchParams] = useSearchParams();
    const [invoices, setInvoices] = useState<InvoicesHistoryData[]>([]);
    const [isConfirmRestore, setIsConfirmRestore] = useState(false);
    const [filterBillTo, setFilterBilTo] = useState<TableFilterOption[]>([]);
    const [rowSelection, setRowSelection] = useState({});
    const [columnSizing, setColumnSizing] = useState({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [openOptionsId, setOpenOptionsId] = useState<string | null>(null);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [billToLoading, setBillToLoading] = useState(true);

    const restoreInvoice = async (invoiceNumber: string) => {
        try {
            await invoicesRepository.restore(db, invoiceNumber);

            setInvoices(invoices.filter((item) => item.invoiceNumber !== invoiceNumber));
            $invoices.set({ isLoad: true, values: await invoicesRepository.getAllUnArchived(db) });

            toast.success("Invoice restored!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
    }
    const deleteInvoice = async (invoiceNumber: string) => {
        try {
            await invoicesRepository.delete(db, invoiceNumber);
            setInvoices(invoices.filter((item) => item.invoiceNumber !== invoiceNumber));
            toast.success(`Invoice ${invoiceNumber} deleted!`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }
    const table = useReactTable({
        data: invoices,
        columns: archivedInvoicesColumns,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
            columnSizing
        },
        meta: {
            type: "Invoices",
            openOptionsId,
            setOpenOptionsId,
            restoreInvoice,
            deleteInvoice
        } as ArchivedInvoicesTableMeta,
        enableRowSelection: true,
        onColumnSizingChange: setColumnSizing,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const restoreMany = async () => {
        try {
            const ids = table.getSelectedRowModel().rows.map(row => row.original.invoiceNumber);
            await invoicesRepository.restoreMany(db, ids);

            setInvoices(invoices.filter((item) => !ids.includes(item.invoiceNumber)));
            $invoices.set({ isLoad: true, values: await invoicesRepository.getAllUnArchived(db) });

            toast.success("Invoices restored!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }

    useEffect(() => {
        try {
            if (!$archivedInvoices.value.isLoad) {
                invoicesRepository.getAllArchived(db).then((res) => {
                    setInvoices(res);
                    $archivedInvoices.set({ isLoad: true, values: res });
                });
            }
            else {
                setIsLoading(false);
                setInvoices($archivedInvoices.value.values);
            }

            if ($customers.value.isLoad) setFilterBilTo($customers.value.values.map(({ name }) => {
                return { value: name, text: `Customer: ${name}` }
            }));
            else customersRepository.getAll(db).then((res) => {
                setFilterBilTo(res.map(({ name }) => {
                    return { value: name, text: `Customer: ${name}` }
                }));

            });
            setBillToLoading(false);

            const customersFilters = searchParams.get("customer");
            if (customersFilters) {
                const customersArray = customersFilters.split(",");
                const column = table.getColumn("billTo");
                column?.setFilterValue(customersArray);
            }
            const timeout = setTimeout(() => {
                setIsLoading(false);
            }, 600);
            return () => clearTimeout(timeout);
        }
        catch (err: any) {
            throw new Error(err.message)
        }
    }, []);

    return <section className="invoices-archive">
        <div className="invoices-archive__container">
            <ToastContainer />
            {isConfirmRestore && <div className="overlay">
                <ConfirmRestore
                    type="Invoice"
                    count={table.getSelectedRowModel().rows.length}
                    onClose={() => setIsConfirmRestore(false)}
                    onRestore={restoreMany} />
            </div>}
            <div className="table-container">
                <div className="table-container__data">
                    <div className="table-container__head">
                        <div className="table__head-container">
                            <h1> Archived Invoices</h1>
                        </div>
                    </div>
                    {(!billToLoading && !isLoading) ? <>
                        <div className="table__toolbar-container">
                            <DataTableToolbar table={table}
                                filtersFields={[{ field: "billTo", title: "Customer", options: filterBillTo }]}
                                searchField="title" />
                            {table.getSelectedRowModel().rows.length > 0 &&
                                <button className="table__restore-button" onClick={() => setIsConfirmRestore(true)}>
                                    <Undo2 width={14} height={14} /> Restore {table.getSelectedRowModel().rows.length} items
                                </button>
                            }
                        </div>
                        <div className="table-container__table-area">
                            <Table>
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                return (
                                                    <TableHead key={header.id} style={{ width: `${header.getSize()}%` }}>
                                                        {header.isPlaceholder ? null
                                                            : flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </TableHead>
                                                )
                                            })}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {table.getRowModel().rows?.length ? (
                                        table.getRowModel().rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                className="table__row"
                                                onClick={() => {
                                                    if (table.getSelectedRowModel().rows.length > 0) {
                                                        return row.toggleSelected()
                                                    }

                                                }}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell key={cell.id} className="table__cell"
                                                        style={{ width: `${cell.column.getSize()}%` }}
                                                        onClick={(e) => {
                                                            const { id } = cell.column;
                                                            if (id === "select" || id === "actions") e.stopPropagation()
                                                        }}>
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <div className="empty">
                                            <div className="empty__inner">
                                                <img src="/Eyes.png" alt="No Data" />
                                                <b>No results</b>
                                            </div>
                                        </div>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </> : <div className="spinner-container">
                        <Spinner />
                    </div>}
                </div>
                <div className="table-container__pagination">
                    <DataTablePagination table={table} />
                </div>
            </div>
        </div>
    </section >
}
